// ============================================================================
// Shared enum/boolean -> chip registry
// ============================================================================
// Single source of truth for how known enum-like DB columns render as tags:
// translated label + semantic color, keyed by column name then raw value.
// Used by individual table pages (TendersPage, FundSystemPage, ...) AND by
// AgentResultTable (the AI agent's generic query-result grid), so a value
// like tenders.type = 'public' always renders as a translated chip everywhere
// it appears, not just on its own dedicated page.
// ============================================================================

export type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

interface EnumDef {
  ar: string;
  en: string;
  color: ChipColor;
}

// column name -> raw stored value -> display definition
export const ENUM_COLUMN_VALUES: Record<string, Record<string, EnumDef>> = {
  // tenders.type
  type: {
    public: { ar: 'عامة', en: 'Public', color: 'info' },
    private: { ar: 'خاصة', en: 'Private', color: 'default' },
    international: { ar: 'دولية', en: 'International', color: 'secondary' },
  },
  // outward_letters.delivery_method
  delivery_method: {
    email: { ar: 'بريد إلكتروني', en: 'Email', color: 'info' },
    fax: { ar: 'فاكس', en: 'Fax', color: 'default' },
    hand_delivery: { ar: 'تسليم باليد', en: 'Hand Delivery', color: 'secondary' },
    post: { ar: 'بريد عادي', en: 'Post', color: 'primary' },
  },
  // fund_system.payment_status
  payment_status: {
    paid: { ar: 'مدفوع', en: 'Paid', color: 'success' },
    pending: { ar: 'معلق', en: 'Pending', color: 'warning' },
    overdue: { ar: 'متأخر', en: 'Overdue', color: 'error' },
    cancelled: { ar: 'ملغى', en: 'Cancelled', color: 'default' },
  },
  // trainings.status
  status: {
    upcoming: { ar: 'قادم', en: 'Upcoming', color: 'info' },
    ongoing: { ar: 'جارٍ', en: 'Ongoing', color: 'warning' },
    completed: { ar: 'مكتمل', en: 'Completed', color: 'success' },
    cancelled: { ar: 'ملغى', en: 'Cancelled', color: 'default' },
  },
};

// Look up the chip label + color for a known enum column/value. Returns null
// for unknown columns/values so callers can fall back to plain text.
export const enumChip = (
  column: string,
  value: unknown,
  locale: 'ar' | 'en',
): { label: string; color: ChipColor } | null => {
  if (value === null || value === undefined || value === '') return null;
  const def = ENUM_COLUMN_VALUES[column]?.[String(value)];
  if (!def) return null;
  return { label: locale === 'ar' ? def.ar : def.en, color: def.color };
};
